// src/pages/Support/SupportTickets.tsx - COMPLETE FIXED VERSION
import React, { useState, useEffect } from 'react';
import { supportAPI, usersAPI } from '../../api';
import { motion } from 'framer-motion';
import { 
  Headphones, User, ShoppingBag, 
  Search, RefreshCw, Eye,
  Send, X, Loader2, Clock
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { formatDisplayId } from '../../utils/idGenerator';

interface Ticket {
  _id: string;
  ticketId: string;
  orderId?: { _id: string; orderId: string };
  customerId: { _id: string; fullName: string; email: string; phone?: string };
  riderId?: { _id: string; fullName: string; email: string; phone?: string };
  supportAgentId?: { _id: string; fullName: string; email: string; profileImage?: string };
  issueType: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  subject: string;
  description: string;
  messages: Array<{
    senderId: string | { _id: string; fullName: string };
    senderRole: 'customer' | 'rider' | 'support' | 'admin';
    message: string;
    createdAt: string;
  }>;
  resolution?: string;
  resolvedAt?: string;
  createdAt: string;
}

const SupportTickets: React.FC = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState<'all' | 'open' | 'in_progress' | 'resolved' | 'closed'>('all');
  const [priorityFilter, setPriorityFilter] = useState<'all' | 'low' | 'medium' | 'high' | 'urgent'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [supportAgents, setSupportAgents] = useState<any[]>([]);
  
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  useEffect(() => {
    fetchTickets();
    fetchSupportAgents();
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [filter, priorityFilter]);

  const fetchTickets = async () => {
    setLoading(true);
    setRefreshing(true);
    try {
      const params: any = {};
      if (filter !== 'all') params.status = filter;
      if (priorityFilter !== 'all') params.priority = priorityFilter;
      
      const res = await supportAPI.getAllTickets(params);
      if (res.data.success && res.data.data) {
        setTickets((res.data.data.tickets || []) as Ticket[]);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchSupportAgents = async () => {
    try {
      const res = await usersAPI.getAll({ role: 'support' });
      if (res.data.success && res.data.data) {
        setSupportAgents(res.data.data.users || []);
      }
    } catch (error) {
      console.error('Error fetching support agents:', error);
    }
  };

  const handleAssignToMe = async (ticketId: string) => {
    try {
      const res = await supportAPI.assignTicket(ticketId, { agentId: user?._id || '' });
      if (res.data.success) {
        toast.success('Ticket assigned to you');
        fetchTickets();
      }
    } catch (error) {
      toast.error('Failed to assign ticket');
    }
  };

  const handleAssignToAgent = async (ticketId: string, agentId: string) => {
    try {
      const res = await supportAPI.assignTicket(ticketId, { agentId });
      if (res.data.success) {
        toast.success('Ticket assigned');
        fetchTickets();
      }
    } catch (error) {
      toast.error('Failed to assign ticket');
    }
  };

  const handleResolve = async (ticketId: string) => {
    const resolution = prompt('Enter resolution (optional):');
    try {
      const res = await supportAPI.resolveTicket(ticketId, { resolution: resolution || '' });
      if (res.data.success) {
        toast.success('Ticket resolved');
        fetchTickets();
      }
    } catch (error) {
      toast.error('Failed to resolve ticket');
    }
  };

  const handleClose = async (ticketId: string) => {
    if (!window.confirm('Close this ticket?')) return;
    try {
      const res = await supportAPI.closeTicket(ticketId);
      if (res.data.success) {
        toast.success('Ticket closed');
        fetchTickets();
      }
    } catch (error) {
      toast.error('Failed to close ticket');
    }
  };

  const handleViewTicket = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setShowTicketModal(true);
    setReplyMessage('');
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;
    
    setSendingReply(true);
    try {
      const res = await supportAPI.addMessage(selectedTicket._id, { message: replyMessage });
      if (res.data.success) {
        toast.success('Reply sent');
        setReplyMessage('');
        
        const updatedRes = await supportAPI.getTicket(selectedTicket._id);
        if (updatedRes.data.success && updatedRes.data.data?.ticket) {
          setSelectedTicket(updatedRes.data.data.ticket as Ticket);
        }
        
        fetchTickets();
      }
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        ticket.ticketId.toLowerCase().includes(query) ||
        formatDisplayId(ticket.ticketId).toLowerCase().includes(query) ||
        ticket.subject.toLowerCase().includes(query) ||
        ticket.customerId?.fullName?.toLowerCase().includes(query) ||
        ticket.riderId?.fullName?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const getStatusBadge = (status: string) => {
    const colors = {
      open: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
      resolved: 'bg-green-100 text-green-800 border-green-200',
      closed: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[status as keyof typeof colors] || colors.open;
  };

  const getPriorityBadge = (priority: string) => {
    const colors = {
      urgent: 'bg-red-100 text-red-800 border-red-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      low: 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[priority as keyof typeof colors] || colors.medium;
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour ago`;
    if (diffDays < 7) return `${diffDays} day ago`;
    return date.toLocaleDateString();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Support Tickets</h1>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchTickets}
            disabled={refreshing}
            className="p-2 rounded-lg bg-white border border-gray-300 hover:border-emerald-400 transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`h-5 w-5 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-4 shadow-lg mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by Ticket ID, Subject, or Customer..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value as any)}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="all">All Priority</option>
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-5 gap-4 mt-4 pt-4 border-t">
          <div className="text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {tickets.filter(t => t.status === 'open').length}
            </div>
            <div className="text-xs text-gray-600">Open</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {tickets.filter(t => t.status === 'in_progress').length}
            </div>
            <div className="text-xs text-gray-600">In Progress</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {tickets.filter(t => t.status === 'resolved').length}
            </div>
            <div className="text-xs text-gray-600">Resolved</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-600">
              {tickets.filter(t => t.status === 'closed').length}
            </div>
            <div className="text-xs text-gray-600">Closed</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">
              {tickets.filter(t => t.priority === 'urgent' && t.status !== 'closed').length}
            </div>
            <div className="text-xs text-gray-600">Urgent</div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="h-12 w-12 animate-spin text-emerald-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading tickets...</p>
        </div>
      ) : filteredTickets.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl">
          <Headphones className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No tickets found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredTickets.map((ticket) => (
            <motion.div
              key={ticket._id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white rounded-2xl p-6 shadow-lg border hover:shadow-xl transition-all cursor-pointer"
              onClick={() => handleViewTicket(ticket)}
            >
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-bold text-lg">Ticket #{formatDisplayId(ticket.ticketId)}</h3>
                    <Badge className={getStatusBadge(ticket.status)}>
                      {ticket.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                    <Badge className={getPriorityBadge(ticket.priority)}>
                      {ticket.priority.toUpperCase()}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-900 font-medium mb-1">{ticket.subject}</p>
                  <p className="text-sm text-gray-600 line-clamp-2 mb-2">{ticket.description}</p>
                  <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <User className="h-3 w-3" />
                      Customer: {ticket.customerId?.fullName}
                    </span>
                    {ticket.riderId && (
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        Rider: {ticket.riderId.fullName}
                      </span>
                    )}
                    {ticket.orderId && (
                      <span className="flex items-center gap-1">
                        <ShoppingBag className="h-3 w-3" />
                        Order: {formatDisplayId(ticket.orderId.orderId)}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {getTimeAgo(ticket.createdAt)}
                    </span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {ticket.status === 'open' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleAssignToMe(ticket._id); }}
                      className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm"
                    >
                      Assign to Me
                    </button>
                  )}
                  {ticket.status === 'in_progress' && (
                    <>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleResolve(ticket._id); }}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                      >
                        Resolve
                      </button>
                      <select
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handleAssignToAgent(ticket._id, e.target.value)}
                        className="px-3 py-2 border rounded-lg text-sm"
                        defaultValue=""
                      >
                        <option value="" disabled>Reassign to...</option>
                        {supportAgents.map(agent => (
                          <option key={agent._id} value={agent._id}>{agent.fullName}</option>
                        ))}
                      </select>
                    </>
                  )}
                  {ticket.status === 'resolved' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleClose(ticket._id); }}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
                    >
                      Close
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleViewTicket(ticket); }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm flex items-center gap-1"
                  >
                    <Eye className="h-4 w-4" />
                    View
                  </button>
                </div>
              </div>

              {ticket.messages && ticket.messages.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs font-medium text-gray-500 mb-2">Latest Activity:</p>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-gray-700">
                        {typeof ticket.messages[ticket.messages.length - 1].senderId === 'object'
                          ? (ticket.messages[ticket.messages.length - 1].senderId as any).fullName
                          : ticket.messages[ticket.messages.length - 1].senderRole}
                      </span>
                      <span className="text-xs text-gray-500">
                        {getTimeAgo(ticket.messages[ticket.messages.length - 1].createdAt)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">
                      {ticket.messages[ticket.messages.length - 1].message}
                    </p>
                  </div>
                </div>
              )}

              {ticket.supportAgentId && (
                <div className="mt-4 flex items-center gap-2 text-xs text-gray-500">
                  <span>Assigned to:</span>
                  <div className="flex items-center gap-1">
                    <div className="h-5 w-5 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 text-[10px] font-bold">
                      {ticket.supportAgentId.fullName.charAt(0)}
                    </div>
                    <span>{ticket.supportAgentId.fullName}</span>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {/* Ticket Detail Modal */}
      {showTicketModal && selectedTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          >
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-green-50">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Ticket #{formatDisplayId(selectedTicket.ticketId)}</h2>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className={getStatusBadge(selectedTicket.status)}>
                      {selectedTicket.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                    <Badge className={getPriorityBadge(selectedTicket.priority)}>
                      {selectedTicket.priority.toUpperCase()}
                    </Badge>
                    <span className="text-sm text-gray-500">
                      Created {new Date(selectedTicket.createdAt).toLocaleString()}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setShowTicketModal(false)}
                  className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Customer Information</h3>
                  <p className="text-sm"><span className="text-gray-500">Name:</span> {selectedTicket.customerId?.fullName}</p>
                  <p className="text-sm"><span className="text-gray-500">Email:</span> {selectedTicket.customerId?.email}</p>
                  {selectedTicket.customerId?.phone && (
                    <p className="text-sm"><span className="text-gray-500">Phone:</span> {selectedTicket.customerId.phone}</p>
                  )}
                </div>

                {selectedTicket.riderId && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Rider Information</h3>
                    <p className="text-sm"><span className="text-gray-500">Name:</span> {selectedTicket.riderId.fullName}</p>
                    <p className="text-sm"><span className="text-gray-500">Email:</span> {selectedTicket.riderId.email}</p>
                    {selectedTicket.riderId.phone && (
                      <p className="text-sm"><span className="text-gray-500">Phone:</span> {selectedTicket.riderId.phone}</p>
                    )}
                  </div>
                )}

                {selectedTicket.orderId && (
                  <div className="bg-gray-50 rounded-xl p-4 md:col-span-2">
                    <h3 className="font-semibold text-gray-900 mb-3">Order Details</h3>
                    <p className="text-sm"><span className="text-gray-500">Order ID:</span> {selectedTicket.orderId.orderId}</p>
                  </div>
                )}
              </div>

              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">Issue Details</h3>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-sm font-medium text-gray-900 mb-2">{selectedTicket.subject}</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedTicket.description}</p>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">Conversation</h3>
                <div className="space-y-4">
                  {selectedTicket.messages && selectedTicket.messages.length > 0 ? (
                    selectedTicket.messages.map((msg, idx) => {
                      const isSupport = msg.senderRole === 'support' || msg.senderRole === 'admin';
                      return (
                        <div key={idx} className={`flex ${isSupport ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[80%] ${isSupport ? 'bg-emerald-600 text-white' : 'bg-white border border-gray-200'} rounded-2xl p-4`}>
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`text-xs font-medium ${isSupport ? 'text-emerald-100' : 'text-gray-500'}`}>
                                {typeof msg.senderId === 'object' ? (msg.senderId as any).fullName : msg.senderRole}
                              </span>
                              <span className={`text-xs ${isSupport ? 'text-emerald-200' : 'text-gray-400'}`}>
                                {new Date(msg.createdAt).toLocaleTimeString()}
                              </span>
                            </div>
                            <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-center text-gray-500 py-4">No messages yet</p>
                  )}
                </div>
              </div>

              {selectedTicket.status === 'resolved' && selectedTicket.resolution && (
                <div className="mb-6 p-4 bg-green-50 rounded-xl border border-green-200">
                  <h3 className="font-semibold text-green-800 mb-2">Resolution</h3>
                  <p className="text-sm text-green-700">{selectedTicket.resolution}</p>
                  {selectedTicket.resolvedAt && (
                    <p className="text-xs text-green-600 mt-2">
                      Resolved on {new Date(selectedTicket.resolvedAt).toLocaleString()}
                    </p>
                  )}
                </div>
              )}

              {selectedTicket.status !== 'closed' && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-3">Add Reply</h3>
                  <div className="flex gap-3">
                    <Input
                      type="text"
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Type your reply..."
                      className="flex-1"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendReply();
                        }
                      }}
                    />
                    <Button
                      onClick={handleSendReply}
                      disabled={!replyMessage.trim() || sendingReply}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      {sendingReply ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex flex-wrap gap-3">
                {selectedTicket.status === 'open' && (
                  <Button
                    onClick={() => {
                      handleAssignToMe(selectedTicket._id);
                      setShowTicketModal(false);
                    }}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    Assign to Me
                  </Button>
                )}
                {selectedTicket.status === 'in_progress' && (
                  <Button
                    onClick={() => {
                      handleResolve(selectedTicket._id);
                      setShowTicketModal(false);
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    Resolve Ticket
                  </Button>
                )}
                {selectedTicket.status === 'resolved' && (
                  <Button
                    onClick={() => {
                      handleClose(selectedTicket._id);
                      setShowTicketModal(false);
                    }}
                    className="bg-gray-600 hover:bg-gray-700 text-white"
                  >
                    Close Ticket
                  </Button>
                )}
                <Button
                  onClick={() => setShowTicketModal(false)}
                  variant="outline"
                  className="border-gray-300"
                >
                  Close
                </Button>
              </div>

              {selectedTicket.supportAgentId && (
                <p className="text-xs text-gray-500 mt-3">
                  Assigned to: {selectedTicket.supportAgentId.fullName}
                </p>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SupportTickets;